import { useEffect } from 'react';
import type { RecipientCheckResult, RecipientMismatch } from '@/types';

/**
 * The inline warning, shown while editing.
 *
 * Renders nothing unless there is something to say. In particular it stays silent when the check
 * could not be made — a report with no client parameter, or a client with nobody on record — because
 * "we could not tell" is not a finding, and showing it as one would train people to ignore the box.
 */
export function RecipientMismatchNotice({ check }: { check: RecipientCheckResult | null }) {
  if (!check?.checked || check.mismatches.length === 0) return null;

  return (
    <div className="notice notice-warn" style={{ marginTop: 14, marginBottom: 0 }}>
      <strong>
        {check.mismatches.length === 1
          ? 'One recipient does not belong to this report’s client.'
          : `${check.mismatches.length} recipients do not belong to this report’s client.`}
      </strong>
      <MismatchBody check={check} />
    </div>
  );
}

/**
 * Shown when a save was refused for an unconfirmed mismatch.
 *
 * Deliberately a decision rather than a dismissible warning: the two buttons say what each one
 * does, "Send anyway" is not the primary, and choosing it is recorded against the person who chose
 * it. The point is that a misdirected report should cost one deliberate click, not zero.
 */
export function RecipientConfirmDialog({
  check,
  busy,
  onConfirm,
  onCancel
}: {
  check: RecipientCheckResult;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="modal-backdrop" role="presentation" onClick={onCancel}>
      <div
        className="modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="recipient-confirm-title"
        onClick={e => e.stopPropagation()}>
        <h3 id="recipient-confirm-title">Check who this report is going to</h3>
        <p style={{ color: 'var(--kt-grey-700)', fontSize: '.92rem', marginBottom: 12 }}>
          This report will contain <strong>client {check.clientScope.join(', ')}</strong>&rsquo;s data.
          {check.mismatches.length === 1
            ? ' One of its recipients is not at a domain on record for that client:'
            : ' Some of its recipients are not at domains on record for that client:'}
        </p>

        <MismatchBody check={check} />

        <div className="modal-actions">
          <button className="btn-primary" onClick={onCancel} disabled={busy}>
            Go back and check
          </button>
          <button className="btn-danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Saving…' : 'Send anyway'}
          </button>
        </div>
        <div style={{ fontSize: '.78rem', color: 'var(--kt-grey-500)', marginTop: 10 }}>
          Sending anyway is recorded in the audit log against your name.
        </div>
      </div>
    </div>
  );
}

/** The list of offending addresses plus what the client's domains actually are. */
function MismatchBody({ check }: { check: RecipientCheckResult }) {
  return (
    <>
      <ul className="mismatch-list">
        {check.mismatches.map(m => (
          <li key={`${m.field}-${m.email}`}>
            <code>{m.email}</code>
            <span className="mismatch-note">{describe(m)}</span>
          </li>
        ))}
      </ul>
      {check.knownDomains.length > 0 && (
        <div style={{ fontSize: '.82rem', color: 'var(--kt-grey-500)', marginTop: 8 }}>
          On record for client {check.clientScope.join(', ')}:{' '}
          {check.knownDomains.slice(0, 6).map(d => <code key={d} style={{ marginRight: 6 }}>{d}</code>)}
          {check.knownDomains.length > 6 && <>and {check.knownDomains.length - 6} more</>}
        </div>
      )}
    </>
  );
}

function describe(m: RecipientMismatch): string {
  const where = m.field === 'To' ? '' : ` (${m.field})`;
  return m.kind === 'PersonalWebmail'
    ? `personal webmail address${where}`
    : `${m.domain} is not on record for this client${where}`;
}
